/**
 * Agent Theme Utility
 * Sets the --agent-primary-color CSS variable based on the agent's button_color setting.
 * Default fallback: #131f37
 */

const DEFAULT_AGENT_COLOR = '#131f37';

/**
 * Validates if a string is a valid hex color.
 * Accepts formats: #RGB, #RRGGBB, #RRGGBBAA
 * 
 * @param {string} color - The color string to validate
 * @returns {boolean} True if valid hex color
 */
const isValidHexColor = (color) => {
  if (!color || typeof color !== 'string') return false;
  // Remove any leading/trailing whitespace
  const trimmed = color.trim();
  // Valid hex color regex: # followed by 3, 4, 6, or 8 hex characters
  const hexRegex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/;
  return hexRegex.test(trimmed);
};

/**
 * Sanitizes a color value - fixes common issues like double hash.
 * 
 * @param {string} color - The color string to sanitize
 * @returns {string|null} Sanitized color or null if invalid
 */
const sanitizeHexColor = (color) => {
  if (!color || typeof color !== 'string') return null;
  
  let sanitized = color.trim();
  
  // Fix double hash issue (##131f37 -> #131f37)
  while (sanitized.startsWith('##')) {
    sanitized = sanitized.substring(1);
  }
  
  // Add hash if missing
  if (sanitized && !sanitized.startsWith('#') && /^[A-Fa-f0-9]{3,8}$/.test(sanitized)) {
    sanitized = '#' + sanitized;
  }
  
  return isValidHexColor(sanitized) ? sanitized : null;
};

/**
 * Sets the agent's primary color as a CSS variable on the document root.
 * This color is used by all .btn-agent-primary buttons.
 * Validates and sanitizes the color, falling back to default if invalid.
 * 
 * @param {string|null|undefined} buttonColor - The agent's button color hex value
 */
export const setAgentThemeColor = (buttonColor) => {
  const sanitizedColor = sanitizeHexColor(buttonColor);
  const color = sanitizedColor || DEFAULT_AGENT_COLOR;
  document.documentElement.style.setProperty('--agent-primary-color', color);
};

/**
 * Gets the current agent primary color from CSS variable or returns default.
 * 
 * @returns {string} The current agent primary color
 */
export const getAgentThemeColor = () => {
  const color = getComputedStyle(document.documentElement).getPropertyValue('--agent-primary-color').trim();
  return color || DEFAULT_AGENT_COLOR;
};

/**
 * Resets the agent theme color to the default value.
 */
export const resetAgentThemeColor = () => {
  document.documentElement.style.setProperty('--agent-primary-color', DEFAULT_AGENT_COLOR);
};

export default {
  setAgentThemeColor,
  getAgentThemeColor,
  resetAgentThemeColor,
  DEFAULT_AGENT_COLOR,
  isValidHexColor,
  sanitizeHexColor
};
